import { RE2JS } from 're2js'

/**
 * Characters that are invalid or unsafe in filenames on common filesystems
 * (Windows, macOS, Linux).
 */
const UNSAFE_FILENAME_CHARS = /[/\\:*?"<>|,]/g

const DEFAULT_OPTIONS = {
    maxLength: 50,
    replacement: '-',
    fallback: 'file',
} as const

export type SafeFilenameOptions = {
    maxLength?: number
    replacement?: string
    fallback?: string
}

/**
 * Sanitizes a string for safe use as a filename (or filename segment).
 * Replaces unsafe filesystem characters and whitespace, truncates length,
 * and collapses repeated replacements.
 *
 * @param value - Raw string (e.g. user-provided title)
 * @param options - maxLength, replacement character, fallback when value is empty
 * @returns Safe filename-safe string
 */
export default function safeFilename(
    value: string | null | undefined,
    options: SafeFilenameOptions = {},
): string {
    const { maxLength, replacement, fallback } = {
        ...DEFAULT_OPTIONS,
        ...options,
    }

    const str = typeof value === 'string' ? value.trim() : ''
    if (!str) {
        return fallback
    }

    const repl = typeof replacement === 'string' ? replacement : '-'
    let safe = str.replace(UNSAFE_FILENAME_CHARS, repl).replace(/\s+/g, repl)

    if (maxLength > 0) {
        safe = safe.slice(0, maxLength)
    }

    // Collapse repeated replacement chars and trim replacement from edges
    const replEscaped = repl.replace(/[.*+?^${}()|[\]\\]/g, '\\$0')
    safe = RE2JS.compile(`${replEscaped}+`).matcher(safe).replaceAll(repl)
    safe = RE2JS.compile(`^${replEscaped}+|${replEscaped}+$`)
        .matcher(safe)
        .replaceAll('')

    return safe || fallback
}
